import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import type { SyntheticEvent } from "react";
import { scheduleIdleWork } from "utils/scheduleIdleWork";
import { createReplayPlanCopy } from "./createReplayPlanCopy";
import type { PlanImageProps, ReplayPlanCopyState } from "./types";

export const PlanImage = forwardRef<HTMLImageElement, PlanImageProps>(
  ({ src, alt, style, onLoad }, ref) => {
    const [replayPlanCopy, setReplayPlanCopy] = useState<ReplayPlanCopyState>({
      phase: "waitingForLoad",
    });

    const handleLoad = useCallback(
      (event: SyntheticEvent<HTMLImageElement>) => {
        const loadedImage = event.currentTarget;
        setReplayPlanCopy({
          phase: "creating",
          image: {
            sourceUrl: src,
            naturalWidth: loadedImage.naturalWidth,
            naturalHeight: loadedImage.naturalHeight,
          },
        });
        onLoad();
      },
      [src, onLoad],
    );

    const imageToCopy = useMemo(
      () => (replayPlanCopy.phase === "creating" ? replayPlanCopy.image : null),
      [replayPlanCopy],
    );

    // A copy of the previous plan must never show under a plan that failed to load
    const currentPlanCopy = useMemo(
      () =>
        replayPlanCopy.phase === "ready" &&
        replayPlanCopy.image.sourceUrl === src
          ? replayPlanCopy
          : null,
      [replayPlanCopy, src],
    );

    useEffect(() => {
      if (imageToCopy === null || imageToCopy.sourceUrl !== src) return;

      const abortController = new AbortController();
      const idleWork = scheduleIdleWork(async () => {
        const dataUrl = await createReplayPlanCopy({
          ...imageToCopy,
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return;

        setReplayPlanCopy(
          dataUrl === null
            ? { phase: "failed" }
            : { phase: "ready", image: imageToCopy, dataUrl },
        );
      });

      return () => {
        idleWork.cancel();
        abortController.abort();
      };
    }, [imageToCopy, src]);

    return (
      <>
        {currentPlanCopy !== null && (
          <img
            src={currentPlanCopy.dataUrl}
            alt=""
            aria-hidden
            className="absolute top-0 left-0 max-w-none select-none pointer-events-none"
            style={{
              ...style,
              width: currentPlanCopy.image.naturalWidth,
              height: currentPlanCopy.image.naturalHeight,
            }}
            draggable={false}
          />
        )}
        <img
          crossOrigin="anonymous"
          ref={ref}
          src={src}
          alt={alt}
          className="relative max-w-none select-none"
          style={style}
          onLoad={handleLoad}
          draggable={false}
        />
      </>
    );
  },
);
